import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { formatMoney, formatNumber, formatPrice } from '@/lib/format';
import { signup_path } from '@/rails/routes';
import type { Plan } from '@/types/platform';
import { Link } from '@inertiajs/react';
import { useState, type ReactNode } from 'react';

const CONTAINER = 'mx-auto max-w-6xl px-4 sm:px-6';

// The stock toggle marks the chosen item with ink on bronze, which is hard to read.
const TOGGLE_ITEM =
  'px-5 hover:bg-secondary hover:text-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary data-[state=on]:hover:text-primary-foreground';

type BillingPeriod = 'month' | 'year';

interface Props {
  plans: Plan[];
  signupOpen: boolean;
}

interface FeatureRow {
  label: string;
  value: string;
  // A plain "No" is shown quieter. The word still carries the meaning.
  off: boolean;
}

const SENDER_LABELS: Record<Plan['features']['sender'], string> = {
  shared: 'Shared sender',
  from_name: 'Your from-name',
  domain: 'Your sending domain',
};

const DEVELOPER_LABELS: Record<Plan['features']['developer'], string> = {
  none: 'No',
  read: 'Read-only',
  write: 'Write SQL behind a confirm',
};

function plural(count: number, one: string, many: string): string {
  return `${formatNumber(count)} ${count === 1 ? one : many}`;
}

function databaseSize(megabytes: number): string {
  if (megabytes >= 1024) {
    return `${Number((megabytes / 1024).toFixed(1))} GB`;
  } else {
    return `${formatNumber(megabytes)} MB`;
  }
}

// "Shared database, 500 MB" / "Dedicated database, 2 GB" / "Dedicated database, your region".
// No size means the database is not capped, so no size is promised.
function databaseLine(plan: Plan): string {
  const parts = [plan.features.dedicatedDatabase ? 'Dedicated database' : 'Shared database'];

  if (plan.databaseMb !== null) parts.push(databaseSize(plan.databaseMb));
  if (plan.features.regionChoice) parts.push('your region');

  return parts.join(', ');
}

function limitLines(plan: Plan): string[] {
  const lines = [
    plural(plan.appLimit, 'app', 'apps'),
    databaseLine(plan),
    `${formatNumber(plan.storageGb)} GB of photos and files`,
    `${plural(plan.emailLimit, 'email', 'emails')} a month`,
  ];

  // No cap set means no number to promise, so the line is left out.
  if (plan.buildCreditLimit !== null) {
    lines.push(`${plural(plan.buildCreditLimit, 'build credit', 'build credits')} a month`);
  }

  return lines;
}

function yesNo(label: string, included: boolean): FeatureRow {
  return { label, value: included ? 'Yes' : 'No', off: !included };
}

function featureRows(plan: Plan): FeatureRow[] {
  const { features } = plan;
  const nativeBuilds = features.nativeBuildsPerQuarter;

  return [
    {
      label: 'CAP Rock Labs watermark',
      value: features.watermark ? 'On' : 'Off',
      off: false,
    },
    yesNo('Custom domain', features.customDomain),
    {
      label: 'Payments',
      value: features.livePayments ? 'Stripe and Square, live' : 'Stripe, test only',
      off: false,
    },
    yesNo('QuickBooks', features.quickbooks),
    { label: 'Email sender', value: SENDER_LABELS[features.sender], off: false },
    {
      label: 'Developer',
      value: DEVELOPER_LABELS[features.developer],
      off: features.developer === 'none',
    },
    yesNo('Git export', features.gitExport),
    yesNo('Claim-project handoff', features.claimProject),
    yesNo('Exception review', features.exceptionReview),
    {
      label: 'Native build credit',
      value: nativeBuilds > 0 ? `${formatNumber(nativeBuilds)} a quarter` : 'No',
      off: nativeBuilds <= 0,
    },
  ];
}

// "Start" / "Start and Trial" / "Start, Trial, and Pilot".
function listNames(names: string[]): string {
  if (names.length <= 2) {
    return names.join(' and ');
  } else {
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }
}

interface EyebrowProps {
  children: ReactNode;
}

// The bronze is the rule only. At this size bronze text on paper is too faint to read.
const Eyebrow = ({ children }: EyebrowProps) => (
  <div className="flex items-center gap-3">
    <span className="bg-accent h-px w-6 shrink-0" aria-hidden="true" />
    <span className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
      {children}
    </span>
  </div>
);

interface PlanCardProps {
  plan: Plan;
  period: BillingPeriod;
  signupLabel: string;
}

const PlanCard = ({ plan, period, signupLabel }: PlanCardProps) => {
  const yearly = period === 'year';
  const priceCents = yearly ? plan.yearlyPriceCents : plan.monthlyPriceCents;
  const yearlySavingsCents = plan.monthlyPriceCents * 12 - plan.yearlyPriceCents;

  return (
    <article
      aria-labelledby={`${plan.key}-name`}
      className="bg-card flex flex-col gap-6 border p-5 sm:p-6"
    >
      <header className="flex flex-col gap-3">
        <h2 id={`${plan.key}-name`} className="text-xl font-medium tracking-tight">
          {plan.name}
        </h2>
        {plan.paid ? (
          <>
            <p className="text-muted-foreground">
              <span className="authority-display text-foreground text-5xl leading-none tabular-nums">
                {formatPrice(priceCents)}
              </span>{' '}
              {yearly ? 'a year' : 'a month'}
            </p>
            <p className="text-muted-foreground min-h-10 text-sm leading-snug">
              {yearly ? (
                <>
                  That is {formatPrice(Math.round(plan.yearlyPriceCents / 12))} a month.
                  {yearlySavingsCents > 0
                    ? ` You save ${formatPrice(yearlySavingsCents)} a year over paying monthly.`
                    : null}
                </>
              ) : (
                'Billed each month.'
              )}
            </p>
          </>
        ) : (
          <>
            <p>
              <span className="authority-display text-5xl leading-none">Free</span>
            </p>
            <p className="text-muted-foreground min-h-10 text-sm leading-snug">
              No charge, monthly or yearly.
            </p>
          </>
        )}
      </header>

      <Button asChild size="lg" className="w-full">
        <Link href={signup_path()}>{signupLabel}</Link>
      </Button>

      <div className="flex flex-col gap-2">
        <h3 className="text-muted-foreground text-sm">How much you get</h3>
        <ul className="border-t">
          {limitLines(plan).map((line) => (
            <li key={line} className="border-b py-2 text-sm leading-snug">
              {line}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-muted-foreground text-sm">What is turned on</h3>
        <dl className="border-t">
          {featureRows(plan).map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[minmax(0,1fr)_minmax(0,auto)] gap-4 border-b py-2 text-sm leading-snug"
            >
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd
                className={row.off ? 'text-muted-foreground text-right' : 'text-right font-medium'}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </article>
  );
};

const Show = ({ plans, signupOpen }: Props) => {
  const [period, setPeriod] = useState<BillingPeriod>('month');
  const signupLabel = signupOpen ? 'Get started' : 'Request access';

  // Free plans stop at their limits. A paid plan bills overage only when both of its rates are set.
  const hardStopPlans = plans.filter((plan) => !plan.paid);
  // Public signup puts a new company on the plan keyed "start" (User.sign_up).
  const startingPlan = plans.find((plan) => plan.key === 'start');

  return (
    <div className="bg-background text-foreground">
      <section className={`${CONTAINER} py-14 sm:py-20`}>
        <div className="flex max-w-2xl flex-col gap-6">
          <Eyebrow>Pricing</Eyebrow>
          <h1 className="authority-display text-4xl leading-[1.08] text-balance sm:text-6xl">
            Plans and what each one includes.
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Every plan builds the same kind of app. They differ in how much you get and what is
            turned on.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span id="billing-period-label" className="text-sm font-medium">
            Show prices
          </span>
          <ToggleGroup
            type="single"
            variant="outline"
            value={period}
            onValueChange={(value) => {
              // Pressing the chosen item again sends an empty value. Keep the choice instead.
              if (value === 'month' || value === 'year') setPeriod(value);
            }}
            aria-labelledby="billing-period-label"
          >
            <ToggleGroupItem value="month" className={TOGGLE_ITEM}>
              Monthly
            </ToggleGroupItem>
            <ToggleGroupItem value="year" className={TOGGLE_ITEM}>
              Yearly
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {plans.length > 0 ? (
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            {plans.map((plan) => (
              <PlanCard key={plan.key} plan={plan} period={period} signupLabel={signupLabel} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground mt-8 border-t pt-6">Plans are not listed yet.</p>
        )}

        <div className="mt-10 flex max-w-3xl flex-col gap-3 border-t pt-6 text-sm leading-relaxed">
          <p>
            Connectors are included. Payments go to your own Stripe or Square account. CAP Rock Labs
            takes no cut.
          </p>
          {hardStopPlans.length > 0 ? (
            <p className="text-muted-foreground">
              {listNames(hardStopPlans.map((plan) => plan.name))}{' '}
              {hardStopPlans.length === 1
                ? 'is a hard stop at its limits'
                : 'are hard stops at their limits'}
              . Nothing is billed past them.
            </p>
          ) : null}
          {plans.map((plan) =>
            plan.storageOverageCentsPerGb !== null && plan.emailOverageCentsPerThousand !== null ? (
              <p key={plan.key} className="text-muted-foreground">
                {plan.name} keeps running past its limits and bills the overage:{' '}
                {formatMoney(plan.storageOverageCentsPerGb)} per GB of files and{' '}
                {formatMoney(plan.emailOverageCentsPerThousand)} per 1,000 emails.
              </p>
            ) : null
          )}
          {startingPlan ? (
            <p className="text-muted-foreground">
              A new account begins on {startingPlan.name}. You change plans from Plan and usage once
              you are in.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default Show;
